/**
 * Module WorkspaceHomeController - Điều khiển hiển thị giao diện trung tâm Workspace Homepage
 * Ngụy trang thành cổng làm việc Coogle Wordspeca
 * Quản lý:
 * 1. Banner chào mừng theo thời gian thực
 * 2. Thẻ Continue Card (Tiếp tục phiên làm việc dở)
 * 3. Lưới ứng dụng App Launcher
 * 4. Bảng tài liệu gần đây (Recent Activities)
 * 5. Tìm kiếm nhanh Workspace
 */
import { appRegistry } from '../../core/workspace/appRegistry.js';
import { sessionManager } from '../../core/workspace/sessionManager.js';

function getDisplayTitle(item) {
  const isVideo = item?.type === 'video' || item?.appId === 'canva' || item?.appId === 'capcut';
  const title = isVideo
    ? (item.rawTitle || item.title || item.disguiseTitle)
    : (item.title || item.disguiseTitle);
  return typeof title === 'string' ? title : '';
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, character => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  })[character]);
}

export class WorkspaceHomeController {
  /**
   * @param {Object} options
   * @param {import('../appRouter.js').AppRouter} [options.router]
   */
  constructor(options = {}) {
    this.router = options.router || null;

    // Các phần tử DOM của Workspace Homepage
    this.container = document.getElementById('view-workspace-home');
    this.greetingTextEl = document.getElementById('ws-greeting-text');
    this.continueSectionEl = document.getElementById('ws-continue-section');
    this.appGridEl = document.getElementById('ws-app-grid');
    this.recentListEl = document.getElementById('ws-recent-list');
    this.searchInput = document.getElementById('ws-search-input');
    this.emptyRecentEl = document.getElementById('ws-recent-empty');

    this.bindEvents();
  }

  /**
   * Gán router sau khi khởi tạo xong AppRouter
   * @param {import('../appRouter.js').AppRouter} router 
   */
  setRouter(router) {
    this.router = router;
  }

  /**
   * Gắn các sự kiện tương tác trên Homepage
   */
  bindEvents() {
    // Search bar opens the content search dialog instead of filtering workspace items.
    if (this.searchInput) {
      this.searchInput.addEventListener('click', () => this.openContentSearch());
      this.searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.openContentSearch();
        }
      });
    }

    // 2. Nút trợ giúp / Bảng phím tắt trên Header
    const btnHelp = document.getElementById('ws-btn-help');
    if (btnHelp) {
      btnHelp.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (typeof window.openHotkeyModal === 'function') {
          window.openHotkeyModal();
        } else {
          const m = document.getElementById('hotkey-guide-modal');
          if (m) m.style.display = 'flex';
        }
      });
    }
  }

  /**
   * Làm mới toàn bộ dữ liệu và render lại giao diện Homepage
   */
  refresh() {
    this.renderGreeting();
    this.renderContinueCard();
    this.renderAppGrid();
    this.renderRecentList();
  }

  /**
   * 1. Render lời chào theo khung giờ trong ngày
   */
  renderGreeting() {
    if (!this.greetingTextEl) return;

    const hour = new Date().getHours();
    let greeting = 'Chúc một ngày làm việc hiệu quả';

    if (hour >= 5 && hour < 12) {
      greeting = 'Chào buổi sáng, chúc bạn làm việc hứng khởi';
    } else if (hour >= 12 && hour < 18) {
      greeting = 'Chào buổi chiều, chúc bạn một ngày suôn sẻ';
    } else {
      greeting = 'Chào buổi tối, chúc bạn thư giãn và hiệu quả';
    }

    this.greetingTextEl.textContent = `${greeting} 👋`;
  }

  /**
   * 2. Render khối "Tiếp tục công việc dở" (Continue Card)
   */
  renderContinueCard() {
    if (!this.continueSectionEl) return;

    const session = sessionManager.getLastActiveSession();

    // Nếu chưa có phiên làm việc nào dở -> Ẩn khối Continue
    if (!session || !session.appId) {
      this.continueSectionEl.style.display = 'none';
      return;
    }

    const appInfo = appRegistry.getAppById(session.appId);
    const appName = appInfo?.name || 'Ứng dụng';
    const appColor = appInfo?.themeColor || '#1a73e8';
    const appIcon = appInfo?.iconSvg || '';
    const displayTitle = getDisplayTitle(session);

    // Định dạng thời gian hoạt động gần nhất
    const timeAgo = this.formatTimeAgo(session.lastActiveAt);

    this.continueSectionEl.style.display = 'block';
    this.continueSectionEl.innerHTML = `
      <div class="ws-section-header">
        <div class="ws-section-title">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/></svg>
          <span>Tiếp tục tài liệu đang làm dở</span>
        </div>
      </div>

      <div class="ws-continue-card" id="ws-continue-btn">
        <div class="ws-continue-left">
          <div class="ws-continue-icon" style="background:${appColor}15; border-color:${appColor}30;">
            ${session.thumbnail ? `<img src="${session.thumbnail}" class="ws-continue-thumb" />` : appIcon}
          </div>
          <div class="ws-continue-info">
            <div class="ws-continue-app-badge" style="color:${appColor};">
              <span class="ws-app-dot" style="background:${appColor};"></span>
              ${appName} • ${timeAgo}
            </div>
            <div class="ws-continue-title" title="${escapeHtml(displayTitle)}">
              ${escapeHtml(displayTitle)}
            </div>
            <div class="ws-continue-subtitle">${session.subtitle || ''}</div>
          </div>
        </div>

        <div class="ws-continue-right">
          <div class="ws-progress-box">
            <div class="ws-progress-text">${session.progress === null ? 'Chưa có tổng số chương' : `Tiến độ: <strong>${session.progress || 0}%</strong>`}</div>
            <div class="ws-progress-bar">
              <div class="ws-progress-fill" style="width:${session.progress || 0}%; background:${appColor};"></div>
            </div>
          </div>
          <button class="ws-btn-resume" style="background:${appColor};">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
            <span>Mở ngay</span>
          </button>
        </div>
      </div>
    `;

    // Gắn sự kiện click mở lại phiên làm việc
    const continueBtn = document.getElementById('ws-continue-btn');
    if (continueBtn) {
      continueBtn.addEventListener('click', () => {
        this.resumeSession(session);
      });
    }
  }

  /**
   * Khôi phục phiên làm việc dở khi người dùng bấm vào Continue Card
   * @param {Object} session 
   */
  resumeSession(session) {
    if (!session || !session.appId) return;

    console.log('[WorkspaceHome] Khôi phục phiên làm việc:', session);

    if (session.type === 'reader') {
      const pos = session.position || {};
      const targetRoute = `#/${session.appId}?source=${encodeURIComponent(pos.source || 's1')}&story=${encodeURIComponent(pos.storyId || '')}&chap=${encodeURIComponent(pos.chapterId || '')}`;
      if (this.router) {
        this.router.navigateTo(targetRoute);
      } else {
        window.location.hash = targetRoute;
      }
    } else if (session.type === 'video') {
      const pos = session.position || {};
      const targetRoute = `#/${session.appId}?v=${encodeURIComponent(pos.videoId || '')}&t=${pos.currentTime || 0}`;
      if (this.router) {
        this.router.navigateTo(targetRoute);
      } else {
        window.location.hash = targetRoute;
      }
    }
  }

  /**
   * 3. Render lưới 5 ứng dụng (App Launcher Grid)
   */
  renderAppGrid(filterQuery = '') {
    if (!this.appGridEl) return;

    let apps = appRegistry.getEnabledApps();
    if (filterQuery) {
      const q = filterQuery.toLowerCase();
      apps = apps.filter(a => a.name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q));
    }

    if (apps.length === 0) {
      this.appGridEl.innerHTML = `<div class="ws-empty-msg">Không tìm thấy ứng dụng phù hợp</div>`;
      return;
    }

    this.appGridEl.innerHTML = apps.map(app => `
      <div class="ws-app-card" data-route="${app.route}">
        <div class="ws-app-icon-box" style="background:${app.themeColor}12;">
          ${app.iconSvg}
        </div>
        <div class="ws-app-content">
          <div class="ws-app-header-row">
            <span class="ws-app-name">${app.name}</span>
            ${app.badgeText ? `<span class="ws-app-badge" style="background:${app.themeColor}15; color:${app.themeColor};">${app.badgeText}</span>` : ''}
          </div>
          <div class="ws-app-desc">${app.description}</div>
        </div>
        <div class="ws-app-arrow">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/></svg>
        </div>
      </div>
    `).join('');

    // Gắn sự kiện click vào từng app card
    this.appGridEl.querySelectorAll('.ws-app-card').forEach(card => {
      card.addEventListener('click', () => {
        const route = card.dataset.route;
        if (route) {
          if (this.router) this.router.navigateTo(route);
          else window.location.hash = route;
        }
      });
    });
  }

  /**
   * 4. Render danh sách tài liệu gần đây (Recent Activities)
   */
  renderRecentList(filterQuery = '') {
    if (!this.recentListEl) return;

    let recents = sessionManager.getRecentItems(8);
    if (filterQuery) {
      const q = filterQuery.toLowerCase();
      recents = recents.filter(item =>
        getDisplayTitle(item).toLowerCase().includes(q)
        || String(item.disguiseTitle || '').toLowerCase().includes(q)
      );
    }

    if (recents.length === 0) {
      if (this.emptyRecentEl) this.emptyRecentEl.style.display = 'flex';
      this.recentListEl.innerHTML = '';
      return;
    }

    if (this.emptyRecentEl) this.emptyRecentEl.style.display = 'none';

    this.recentListEl.innerHTML = recents.map(item => {
      const displayTitle = escapeHtml(getDisplayTitle(item));
      return `
      <div class="ws-recent-row" data-route="${item.route}">
        <div class="ws-recent-main">
          <div class="ws-recent-icon">
            ${item.thumbnail ? `<img src="${item.thumbnail}" class="ws-recent-thumb" />` : (item.iconSvg || '')}
          </div>
          <div class="ws-recent-meta">
            <div class="ws-recent-title" title="${displayTitle}">${displayTitle}</div>
            <div class="ws-recent-sub">${item.subtitle || ''}</div>
          </div>
        </div>
        <div class="ws-recent-app-name">${item.appName || 'Ứng dụng'}</div>
        <div class="ws-recent-time">${this.formatTimeAgo(item.lastActiveAt)}</div>
        <div class="ws-recent-actions">
          <button class="ws-btn-open-recent" title="Mở tài liệu">Mở</button>
        </div>
      </div>
    `;
    }).join('');

    // Gắn sự kiện click mở tài liệu gần đây
    this.recentListEl.querySelectorAll('.ws-recent-row').forEach(row => {
      row.addEventListener('click', () => {
        const route = row.dataset.route;
        if (route) {
          if (this.router) this.router.navigateTo(route);
          else window.location.hash = route;
        }
      });
    });
  }

  /**
   * Xử lý tìm kiếm nhanh trên thanh search của Workspace
   * @param {string} query 
   */
  handleSearch(query) {
    const q = (query || '').trim();
    this.renderAppGrid(q);
    this.renderRecentList(q);
  }

  openContentSearch() {
    if (!window.searchModal || typeof window.searchModal.open !== 'function') return;
    window.searchModal.open(this.searchInput ? this.searchInput.value.trim() : '');
  }

  /**
   * Định dạng thời gian trôi qua tương đối ("vừa xong", "15 phút trước", "Hôm qua")
   * @param {number} timestamp 
   * @returns {string}
   */
  formatTimeAgo(timestamp) {
    if (!timestamp || isNaN(timestamp)) return 'Gần đây';

    const diffSec = Math.floor((Date.now() - timestamp) / 1000);
    if (diffSec < 60) return 'Vừa xong';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} phút trước`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} giờ trước`;
    const days = Math.floor(diffSec / 86400);
    if (days === 1) return 'Hôm qua';
    return `${days} ngày trước`;
  }
}
